import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Clock, CheckCircle2, XCircle, Calendar, ChevronLeft,
  ChevronRight, Trash2, Edit2, ArrowRight, Target, BookOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isToday, isSameDay, parseISO } from 'date-fns';
import { toast } from 'sonner';

const STATUS_STYLES = {
  PLANNED:   'bg-muted text-muted-foreground border-border',
  COMPLETED: 'bg-green-50 text-green-700 border-green-200',
  SKIPPED:   'bg-red-50 text-red-700 border-red-200',
};

const STATUS_LABELS = {
  PLANNED: 'Planned',
  COMPLETED: 'Done',
  SKIPPED: 'Skipped',
};

const DURATIONS = [30, 45, 60, 90, 120, 180];

const projectTypeColors = {
  EE: 'bg-red-50 text-red-700 border-red-200',
  IA: 'bg-blue-50 text-blue-700 border-blue-200',
  TOK_ESSAY: 'bg-amber-50 text-amber-700 border-amber-200',
  TOK_EXHIBITION: 'bg-amber-50 text-amber-700 border-amber-200',
};

const SAT_SECTION_LABELS = {
  READING_WRITING: 'Reading & Writing',
  MATH: 'Math',
  FULL_TEST: 'Full Test',
  GENERAL: 'General',
};

const formatDuration = (mins) => {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const emptyForm = {
  title: '',
  goal: '',
  scheduledDate: format(new Date(), 'yyyy-MM-dd'),
  scheduledTime: '09:00',
  durationMinutes: 60,
  projectId: '',
  satSection: '',
};

export default function StudyPlannerPage() {
  const { api } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));

  const [showForm, setShowForm] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completingSession, setCompletingSession] = useState(null);
  const [progressNote, setProgressNote] = useState('');

  const fetchSessions = useCallback(async () => {
    try {
      const res = await api.get('/work-sessions');
      setSessions(res.data.sessions || []);
    } catch {
      toast.error('Failed to load sessions');
    }
  }, [api]);

  useEffect(() => {
    const load = async () => {
      try {
        const [sessRes, projRes] = await Promise.all([
          api.get('/work-sessions'),
          api.get('/projects'),
        ]);
        setSessions(sessRes.data.sessions || []);
        setProjects(projRes.data.projects || []);
      } catch {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api]);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const sessionsForDay = (day) =>
    sessions.filter((s) => isSameDay(parseISO(s.scheduledAt), day));

  const stats = {
    total: sessions.length,
    completed: sessions.filter((s) => s.status === 'COMPLETED').length,
    skipped: sessions.filter((s) => s.status === 'SKIPPED').length,
    planned: sessions.filter((s) => s.status === 'PLANNED').length,
    rate: sessions.length
      ? Math.round((sessions.filter((s) => s.status === 'COMPLETED').length / sessions.length) * 100)
      : 0,
    totalHours: Math.round(
      sessions.filter((s) => s.status === 'COMPLETED').reduce((acc, s) => acc + s.durationMinutes, 0) / 60 * 10
    ) / 10,
  };

  const openCreate = (date) => {
    setEditingSession(null);
    setForm({
      ...emptyForm,
      scheduledDate: format(date || new Date(), 'yyyy-MM-dd'),
    });
    setShowForm(true);
  };

  const openEdit = (session) => {
    const dt = parseISO(session.scheduledAt);
    setEditingSession(session);
    setForm({
      title: session.title,
      goal: session.goal || '',
      scheduledDate: format(dt, 'yyyy-MM-dd'),
      scheduledTime: format(dt, 'HH:mm'),
      durationMinutes: session.durationMinutes,
      projectId: session.projectId || '',
      satSection: session.satSection || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    const scheduledAt = `${form.scheduledDate}T${form.scheduledTime}:00`;
    const payload = {
      title: form.title.trim(),
      goal: form.goal.trim() || null,
      scheduledAt,
      durationMinutes: Number(form.durationMinutes),
      projectId: form.projectId || null,
      satSection: form.satSection || null,
    };
    try {
      if (editingSession) {
        const res = await api.patch(`/work-sessions/${editingSession.id}`, payload);
        setSessions((prev) =>
          prev.map((s) => (s.id === editingSession.id ? res.data.session : s))
        );
        toast.success('Session updated');
      } else {
        const res = await api.post('/work-sessions', payload);
        setSessions((prev) => [...prev, res.data.session].sort(
          (a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt)
        ));
        toast.success('Session scheduled');
      }
      setShowForm(false);
    } catch {
      toast.error('Failed to save session');
    }
  };

  const handleDelete = async (session) => {
    if (!window.confirm(`Delete "${session.title}"?`)) return;
    try {
      await api.delete(`/work-sessions/${session.id}`);
      setSessions((prev) => prev.filter((s) => s.id !== session.id));
      toast.success('Session deleted');
    } catch {
      toast.error('Failed to delete session');
    }
  };

  const handleSkip = async (session) => {
    try {
      const res = await api.patch(`/work-sessions/${session.id}`, { status: 'SKIPPED' });
      setSessions((prev) => prev.map((s) => (s.id === session.id ? res.data.session : s)));
      toast.success('Marked as skipped');
    } catch {
      toast.error('Failed to update');
    }
  };

  const openComplete = (session) => {
    setCompletingSession(session);
    setProgressNote(session.progressNote || '');
    setShowCompleteDialog(true);
  };

  const handleComplete = async () => {
    try {
      const res = await api.patch(`/work-sessions/${completingSession.id}`, {
        status: 'COMPLETED',
        progressNote: progressNote.trim() || null,
      });
      setSessions((prev) =>
        prev.map((s) => (s.id === completingSession.id ? res.data.session : s))
      );
      toast.success('Session completed!');
      setShowCompleteDialog(false);
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleUnplan = async (session) => {
    try {
      const res = await api.patch(`/work-sessions/${session.id}`, { status: 'PLANNED' });
      setSessions((prev) => prev.map((s) => (s.id === session.id ? res.data.session : s)));
    } catch {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-muted-foreground font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="border-b border-border pb-5 mb-6 flex items-end justify-between">
        <div>
          <p className="label-swiss mb-1">Planning</p>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Study Planner</h1>
        </div>
        <Button onClick={() => openCreate(new Date())} size="sm" className="gap-1.5">
          <Plus className="w-4 h-4" /> New Session
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 border border-border mb-6">
        <StatCell label="Total" value={stats.total} />
        <StatCell label="Planned" value={stats.planned} className="border-l border-border" />
        <StatCell label="Completed" value={stats.completed} className="border-l border-border text-green-700" />
        <StatCell label="Skipped" value={stats.skipped} className="border-l border-border text-red-600" />
        <StatCell label="Hours Done" value={`${stats.totalHours}h`} className="border-l border-border col-span-2 md:col-span-1" />
      </div>

      {/* Week Navigator */}
      <div className="border border-border mb-6">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
          <button onClick={() => setWeekStart((w) => subWeeks(w, 1))} className="p-1 hover:bg-muted rounded">
            <ChevronLeft className="w-4 h-4 text-muted-foreground" />
          </button>
          <p className="label-swiss">
            {format(weekStart, 'MMM d')} – {format(addDays(weekStart, 6), 'MMM d, yyyy')}
          </p>
          <button onClick={() => setWeekStart((w) => addWeeks(w, 1))} className="p-1 hover:bg-muted rounded">
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="grid grid-cols-7 divide-x divide-border">
          {weekDays.map((day) => {
            const daySessions = sessionsForDay(day);
            const today = isToday(day);
            return (
              <div key={day.toISOString()} className={`min-h-[120px] p-2 ${today ? 'bg-primary/5' : ''}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div>
                    <p className={`text-[9px] uppercase tracking-wide font-semibold ${today ? 'text-primary' : 'text-muted-foreground'}`}>
                      {format(day, 'EEE')}
                    </p>
                    <p className={`text-sm font-mono font-bold ${today ? 'text-primary' : 'text-foreground'}`}>
                      {format(day, 'd')}
                    </p>
                  </div>
                  <button
                    onClick={() => openCreate(day)}
                    className="opacity-0 hover:opacity-100 group-hover:opacity-100 p-0.5 hover:bg-muted rounded transition-opacity"
                  >
                    <Plus className="w-3 h-3 text-muted-foreground" />
                  </button>
                </div>
                <div className="space-y-1">
                  {daySessions.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => openEdit(s)}
                      className={`w-full text-left text-[10px] px-1.5 py-1 border rounded leading-tight ${STATUS_STYLES[s.status]} truncate`}
                    >
                      <span className="font-semibold">{format(parseISO(s.scheduledAt), 'HH:mm')}</span>
                      {' '}{s.title}
                    </button>
                  ))}
                  {daySessions.length === 0 && (
                    <button
                      onClick={() => openCreate(day)}
                      className="w-full text-[9px] text-muted-foreground/40 hover:text-muted-foreground py-2 text-center transition-colors"
                    >
                      + add
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Session List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Upcoming */}
        <div className="border border-border">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
            <p className="label-swiss">Upcoming Sessions</p>
          </div>
          <SessionList
            sessions={sessions.filter((s) => s.status === 'PLANNED' && new Date(s.scheduledAt) >= new Date())}
            onEdit={openEdit}
            onDelete={handleDelete}
            onComplete={openComplete}
            onSkip={handleSkip}
            empty="No upcoming sessions planned"
            emptyAction={() => openCreate(new Date())}
          />
        </div>

        {/* History */}
        <div className="border border-border">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
            <CheckCircle2 className="w-3.5 h-3.5 text-muted-foreground" />
            <p className="label-swiss">History</p>
          </div>
          <SessionList
            sessions={sessions
              .filter((s) => s.status !== 'PLANNED' || new Date(s.scheduledAt) < new Date())
              .slice()
              .reverse()
              .slice(0, 20)}
            onEdit={openEdit}
            onDelete={handleDelete}
            onComplete={openComplete}
            onSkip={handleSkip}
            onUnplan={handleUnplan}
            empty="No past sessions yet"
          />
        </div>
      </div>

      {/* Create / Edit Modal */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) setShowForm(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">
              {editingSession ? 'Edit Session' : 'Schedule Study Session'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <label className="label-swiss block mb-1.5">What are you working on? *</label>
              <input
                type="text"
                className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="e.g. Write EE introduction draft"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>

            <div>
              <label className="label-swiss block mb-1.5">Goal — what will you achieve?</label>
              <textarea
                className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                rows={2}
                placeholder="e.g. Complete 300 words of introduction, outline 3 key arguments"
                value={form.goal}
                onChange={(e) => setForm((f) => ({ ...f, goal: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-swiss block mb-1.5">Date *</label>
                <input
                  type="date"
                  className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  value={form.scheduledDate}
                  onChange={(e) => setForm((f) => ({ ...f, scheduledDate: e.target.value }))}
                />
              </div>
              <div>
                <label className="label-swiss block mb-1.5">Time *</label>
                <input
                  type="time"
                  className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  value={form.scheduledTime}
                  onChange={(e) => setForm((f) => ({ ...f, scheduledTime: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <label className="label-swiss block mb-1.5">Duration</label>
              <div className="flex flex-wrap gap-2">
                {DURATIONS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, durationMinutes: d }))}
                    className={`px-2.5 py-1 text-xs font-mono border rounded transition-colors ${
                      form.durationMinutes === d
                        ? 'border-primary bg-primary text-white'
                        : 'border-border text-muted-foreground hover:border-foreground'
                    }`}
                  >
                    {formatDuration(d)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label-swiss block mb-1.5">SAT Prep (optional)</label>
              <select
                className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary bg-white"
                value={form.satSection}
                onChange={(e) => setForm((f) => ({ ...f, satSection: e.target.value }))}
              >
                <option value="">— Not SAT prep —</option>
                {Object.entries(SAT_SECTION_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>

            {projects.length > 0 && (
              <div>
                <label className="label-swiss block mb-1.5">Link to Project (optional)</label>
                <select
                  className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary bg-white"
                  value={form.projectId}
                  onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value }))}
                >
                  <option value="">— None —</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      [{p.type.replace('_', ' ')}] {p.title || p.subject || 'Untitled'}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSave}>
              {editingSession ? 'Save Changes' : 'Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={(open) => { if (!open) setShowCompleteDialog(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Mark as Complete</DialogTitle>
          </DialogHeader>
          {completingSession && (
            <div className="space-y-4 py-1">
              <div className="p-3 bg-muted border border-border text-sm">
                <p className="font-medium text-foreground">{completingSession.title}</p>
                {completingSession.goal && (
                  <p className="text-[11px] text-muted-foreground mt-1">Goal: {completingSession.goal}</p>
                )}
              </div>
              <div>
                <label className="label-swiss block mb-1.5">What did you achieve?</label>
                <textarea
                  className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  rows={3}
                  placeholder="Optional — note what you accomplished in this session"
                  value={progressNote}
                  onChange={(e) => setProgressNote(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowCompleteDialog(false)}>Cancel</Button>
            <Button size="sm" onClick={handleComplete} className="bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SessionList({ sessions, onEdit, onDelete, onComplete, onSkip, onUnplan, empty, emptyAction }) {
  if (sessions.length === 0) {
    return (
      <div className="py-10 text-center">
        <p className="text-sm text-muted-foreground mb-2">{empty}</p>
        {emptyAction && (
          <button onClick={emptyAction} className="text-xs text-primary font-medium uppercase tracking-wide">
            Schedule one →
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="divide-y divide-border max-h-96 overflow-y-auto">
      {sessions.map((s) => {
        const dt = parseISO(s.scheduledAt);
        return (
          <div key={s.id} className="px-4 py-3 hover:bg-muted/20 transition-colors">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="text-[10px] font-mono font-semibold text-muted-foreground">
                    {format(dt, 'EEE MMM d')} · {format(dt, 'HH:mm')}
                  </span>
                  <span className="text-[10px] font-mono text-muted-foreground">
                    {formatDuration(s.durationMinutes)}
                  </span>
                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 border rounded-sm uppercase tracking-wide ${STATUS_STYLES[s.status]}`}>
                    {STATUS_LABELS[s.status]}
                  </span>
                  {s.project && (
                    <span className={`text-[9px] font-bold px-1 py-0.5 border uppercase tracking-wide ${projectTypeColors[s.project.type]}`}>
                      {s.project.type.replace('_', ' ')}
                    </span>
                  )}
                  {s.satSection && (
                    <span className="text-[9px] font-bold px-1 py-0.5 border uppercase tracking-wide bg-primary/5 text-primary border-primary/20">
                      SAT · {SAT_SECTION_LABELS[s.satSection]}
                    </span>
                  )}
                </div>
                <p className="text-sm font-medium text-foreground truncate">{s.title}</p>
                {s.goal && (
                  <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1 flex items-center gap-1">
                    <Target className="w-3 h-3 shrink-0" /> {s.goal}
                  </p>
                )}
                {s.progressNote && (
                  <p className="text-[11px] text-green-600 mt-0.5 line-clamp-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 shrink-0" /> {s.progressNote}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {s.status === 'PLANNED' && (
                  <>
                    <button
                      onClick={() => onComplete(s)}
                      title="Mark complete"
                      className="p-1 rounded hover:bg-green-50 text-muted-foreground hover:text-green-600 transition-colors"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onSkip(s)}
                      title="Skip"
                      className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
                {s.status !== 'PLANNED' && onUnplan && (
                  <button
                    onClick={() => onUnplan(s)}
                    title="Revert to planned"
                    className="p-1 rounded hover:bg-muted text-muted-foreground transition-colors"
                  >
                    <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                  </button>
                )}
                <button
                  onClick={() => onEdit(s)}
                  title="Edit"
                  className="p-1 rounded hover:bg-muted text-muted-foreground transition-colors"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => onDelete(s)}
                  title="Delete"
                  className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatCell({ label, value, className = '' }) {
  return (
    <div className={`py-5 px-5 ${className}`}>
      <p className="text-2xl font-bold font-mono tracking-tight text-foreground">{value}</p>
      <p className="label-swiss mt-1">{label}</p>
    </div>
  );
}
